import { useStyles } from '../styles';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import {
  ControlElement,
  DispatchPropsOfControl,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  RankedTester,
  computeLabel,
  isDescriptionHidden,
} from '@jsonforms/core';
import debounce from 'lodash/debounce';
import { computed, ComputedRef, ref } from 'vue';
import { createEventHook } from '@vueuse/core';
import { Source } from 'src/types/api';
import { sendAnalytics } from 'src/utils/analytics';
import { config } from 'src/utils/config';

export const useControlAppliedOptions = <I extends Input>(input: I) => {
  return computed(() =>
    merge({}, cloneDeep(input.control.value.config), cloneDeep(input.control.value.uischema.options))
  );
};

export const useComputedLabel = <I extends Input>(
  input: I,
  appliedOptions: ComputedRef<Record<string, string | boolean>>
) => {
  return computed((): string => {
    return computeLabel(
      input.control.value.label,
      !!input.control.value.required,
      !!appliedOptions.value?.hideRequiredAsterisk
    );
  });
};

export type Input = {
  control: ComputedRef<{
    cells: {
      tester: RankedTester;
      cell: unknown;
    }[];
    label: string;
    description: string;
    required: boolean;
    i18nKeyPrefix: string;
    uischema: ControlElement;
    errors: string;
    data: unknown;
    rootSchema: JsonSchema;
    id: string;
    config: unknown;
    schema: JsonSchema;
    enabled: boolean;
    visible: boolean;
    path: string;
    renderers: JsonFormsRendererRegistryEntry[];
  }>;
} & DispatchPropsOfControl;

const validation = createEventHook<boolean | undefined>();
const errors = ref<Record<string, string>>({});
export function useWidgetError() {
  return {
    onValidate: validation.on,
    validate: (validate?: boolean) => validation.trigger(validate),
    errors,
    errorMessages: computed(() => Object.values(errors.value).filter(Boolean)),
  };
}

const submissionSuccess = createEventHook<Source>();
const submissionError = createEventHook<unknown>();
const submissionChange = createEventHook<Source>();
export function useSubmission() {
  return {
    onSuccess: submissionSuccess.on,
    onError: submissionError.on,
    onChange: submissionChange.on,
    emitError: (error?: unknown) => submissionError.trigger(error),
    emitSuccess: (submission?: Source) => submission && submissionSuccess.trigger(submission),
    emitChange: (submission?: Source) => submission && submissionChange.trigger(submission),
  };
}
/**
 * Adds styles, isFocused, appliedOptions and onChange
 */
export const useWidgetControl = <I extends Input>(
  input: I,
  // eslint-disable-next-line
  adaptValue: (target: any) => any = (v) => v,
  debounceWait?: number
) => {
  const changeEmitter =
    typeof debounceWait === 'number' ? debounce(input.handleChange, debounceWait) : input.handleChange;

  const onChange = (value: unknown) => {
    changeEmitter(input.control.value.path, adaptValue(value));
    const debouncesendAnalytics = debounce(sendAnalytics, 500);
    debouncesendAnalytics('event', 'form_input', {
      event_category: 'CHANGE',
      event_label: input.control.value.path,
      value: adaptValue(value),
      send_to: env.GA_ID,
    });
  };

  const appliedOptions = useControlAppliedOptions(input);
  const isFocused = ref(false);
  function setIsFocused(val: boolean) {
    isFocused.value = val;
  }
  const persistentHint = (): boolean => {
    return !isDescriptionHidden(
      input.control.value.visible,
      input.control.value.description,
      isFocused.value,
      !!appliedOptions.value?.showUnfocusedDescription
    );
  };
  const computedLabel = useComputedLabel(input, appliedOptions);

  const controlWrapper = computed(() => {
    const { id, description, errors, label, visible, required } = input.control.value;
    return { id, description, errors, label, visible, required };
  });

  const styles = useStyles(input.control.value.uischema, { title: config.value.theme?.components?.title });

  return {
    ...input,
    styles,
    isFocused,
    appliedOptions,
    controlWrapper,
    onChange,
    persistentHint,
    setIsFocused,
    computedLabel,
  };
};
